import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

/**
 * Single upgrade CTA modal used across the entire app.
 *
 * v1 behavior: informational only — tells the beta tester that
 * Pro/Premium will be available soon.  No form, no capture, no action.
 *
 * Phase 2 (post-Stripe): same component, same entry points.  Only the
 * internal content changes to show tier comparison + Stripe Checkout.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {"pro"|"premium"} props.suggestedTier
 * @param {string} props.triggerFeature - for analytics/logging
 */
// eslint-disable-next-line no-unused-vars -- triggerFeature reserved for analytics in Phase 2
export default function UpgradeModal({ open, onClose, suggestedTier = 'pro', triggerFeature }) {
  const overlayRef = useRef(null);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Close on backdrop click
  function handleBackdropClick(e) {
    if (e.target === overlayRef.current) onClose();
  }

  if (!open) return null;

  const tierLabel = suggestedTier === 'premium' ? 'Premium' : 'Pro';

  return (
    <div
      ref={overlayRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50"
    >
      <div className="relative w-full max-w-sm mx-4 rounded-lg border border-[#e2e8f0] dark:border-[#2d3748] bg-white dark:bg-[#1a1f2e] p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-[#111827] dark:text-[#e2e8f0] mb-3">
          {tierLabel} tier launches soon
        </h2>

        <p className="text-[14px] leading-relaxed text-[#4b5563] dark:text-[#94a3b8] mb-5">
          As a beta tester, you'll be among the first to know when {tierLabel}{' '}
          tier is available. Thanks for helping test AI Sports Edge!
        </p>

        <button
          onClick={onClose}
          className="w-full rounded-md bg-[#005BB3] px-4 py-2 text-[14px] font-medium text-white hover:bg-[#004a93] transition-colors"
        >
          Got it
        </button>

        <div className="mt-3 text-center">
          <Link
            to="/pricing"
            onClick={onClose}
            className="text-[13px] text-[#005BB3] dark:text-[#3b82f6] hover:underline"
          >
            See all plan features
          </Link>
        </div>
      </div>
    </div>
  );
}
